import { flexRender, Header } from "@tanstack/react-table";
import { CSSProperties } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ColumnFilterInput } from "./ColumnFilterInput";
import { TableDensity } from "./useTableViewState";

type SortableHeaderCellProps<T> = {
  header: Header<T, unknown>;
  enableColumnOrdering?: boolean;
  enableColumnResizing?: boolean;
  enableColumnFilters?: boolean;
  enableColumnPinning?: boolean;
  enableStickyHeader?: boolean;
  density?: TableDensity;
};

// A single sortable/filterable/resizable/reorderable/pinnable <th>. Drag-and-drop reordering uses
// a dedicated handle so it doesn't conflict with the click-to-sort area.
export function SortableHeaderCell<T>({
  header,
  enableColumnOrdering,
  enableColumnResizing,
  enableColumnFilters,
  enableColumnPinning,
  enableStickyHeader,
  density,
}: SortableHeaderCellProps<T>) {
  const column = header.column;
  const canSort = column.getCanSort();
  const sortDir = column.getIsSorted();
  const toggleSorting = column.getToggleSortingHandler();
  const canFilter =
    enableColumnFilters && !!column.columnDef.meta?.filterVariant;
  const canResize = enableColumnResizing && column.getCanResize();
  const canPin = enableColumnPinning && column.getCanPin();
  const pinnedSide = column.getIsPinned();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id });

  let zIndex: number | undefined;
  if (isDragging) zIndex = 10;
  else if (pinnedSide) zIndex = 3;
  else if (enableStickyHeader) zIndex = 2;

  // Only force TanStack's pixel size when a feature actually depends on it (resizing needs a
  // draggable pixel width; pinning needs DOM widths to match the sticky offset math). Otherwise
  // leave width unset so the column sizes naturally, matching the original Bootstrap table.
  const useFixedWidth = enableColumnResizing || enableColumnPinning;

  const style: CSSProperties = {
    width: useFixedWidth ? header.getSize() : undefined,
    position: enableStickyHeader || pinnedSide ? "sticky" : "relative",
    top: enableStickyHeader ? 0 : undefined,
    left: pinnedSide === "left" ? column.getStart("left") : undefined,
    right: pinnedSide === "right" ? column.getAfter("right") : undefined,
    zIndex,
    background: enableStickyHeader || pinnedSide ? "#fff" : undefined,
    whiteSpace: "nowrap",
    overflow: useFixedWidth ? "hidden" : undefined,
    textOverflow: useFixedWidth ? "ellipsis" : undefined,
    padding: density === "compact" ? "4px 8px" : undefined,
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <th
      ref={setNodeRef}
      style={style}
      className="group"
      aria-sort={
        sortDir === "asc"
          ? "ascending"
          : sortDir === "desc"
            ? "descending"
            : canSort
              ? "none"
              : undefined
      }
    >
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        {enableColumnOrdering ? (
          <button
            {...attributes}
            {...listeners}
            aria-label={`Reorder ${column.id} column`}
            style={{
              background: "none",
              border: "none",
              color: "#999",
              cursor: "grab",
              padding: 0,
            }}
            type="button"
          >
            ⠿
          </button>
        ) : null}
        <span
          onClick={toggleSorting}
          role={canSort ? "button" : undefined}
          tabIndex={canSort ? 0 : undefined}
          onKeyDown={(e) => {
            if (canSort && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              toggleSorting?.(e);
            }
          }}
          style={{
            cursor: canSort ? "pointer" : undefined,
            userSelect: "none",
          }}
        >
          {header.isPlaceholder
            ? null
            : flexRender(column.columnDef.header, header.getContext())}
          {sortDir === "asc" ? " \u25B2" : sortDir === "desc" ? " \u25BC" : ""}
        </span>
        {canPin ? (
          <button
            type="button"
            onClick={() => column.pin(pinnedSide ? false : "left")}
            aria-label={
              pinnedSide
                ? `Unpin ${column.id} column`
                : `Pin ${column.id} column`
            }
            aria-pressed={!!pinnedSide}
            title={pinnedSide ? "Unpin column" : "Pin column to the left"}
            className={
              pinnedSide
                ? "opacity-100"
                : "opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100"
            }
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
              fontSize: 12,
              color: pinnedSide ? "#1976d2" : "#ccc",
            }}
          >
            📌
          </button>
        ) : null}
      </div>

      {canFilter ? <ColumnFilterInput column={column} /> : null}

      {canResize ? (
        <div
          onMouseDown={header.getResizeHandler()}
          onTouchStart={header.getResizeHandler()}
          style={{
            position: "absolute",
            right: 0,
            top: 0,
            height: "100%",
            width: 5,
            cursor: "col-resize",
            userSelect: "none",
            touchAction: "none",
            background: column.getIsResizing() ? "#1976d2" : "transparent",
          }}
        />
      ) : null}
    </th>
  );
}
